/**
 * MfaSetup — página admin configura TOTP (F3-01 AC-01).
 *
 * Flow real D2:
 *   1. Mount → NO auto-POST. Admin hace click "Configurar MFA" (data-testid=mfa-enroll-button).
 *   2. Click → POST /api/mfa/enroll/begin → recibe `{secretId, qrDataUri, otpauthUrl}` (201).
 *   3. <QrTotpSetup> renderiza QR + preview otpauthUrl + input 6 dígitos.
 *   4. Submit code → POST /api/mfa/enroll/confirm body `{secretId, code}` → 200 OK → badge + toast.
 *
 * Rutas válidas (alias):
 *   - `/app/mfa-setup` (F1 original)
 *   - `/profile/security` (alias agregado D2 para match spec mfa-setup-flow)
 *
 * PHI: QR + otpauthUrl contienen secret TOTP (credencial, no PHI clínico).
 * NUNCA persistir en storage, NUNCA loggear.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { ShieldCheck, Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { QrTotpSetup } from '@/components/QrTotpSetup'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import type {
  MfaEnrollBeginResponse,
  MfaEnrollConfirmRequest,
  MfaEnrollConfirmResponse,
} from '@/lib/types/fitlab-fhir'

export default function MfaSetupPage() {
  const navigate = useNavigate()
  const { show } = useToast()
  const { refresh } = useAuth()
  const [enrollData, setEnrollData] = useState<MfaEnrollBeginResponse | null>(null)
  const [enrollError, setEnrollError] = useState<string | null>(null)
  const [verifyError, setVerifyError] = useState<string | null>(null)
  const [activated, setActivated] = useState(false)

  const enrollMutation = useMutation({
    mutationFn: async () => api.post<MfaEnrollBeginResponse>('/api/mfa/enroll/begin'),
    onSuccess: (data) => {
      setEnrollData(data)
      setEnrollError(null)
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? 'No se pudo generar el código QR. Reintenta.'
          : 'Error de conexión con el servidor.'
      setEnrollError(msg)
    },
  })

  const verifyMutation = useMutation({
    mutationFn: async (payload: MfaEnrollConfirmRequest) =>
      api.post<MfaEnrollConfirmResponse>('/api/mfa/enroll/confirm', payload),
    onSuccess: async () => {
      // Estado persistente "MFA activado" (role=alert para a11y + spec AC-01).
      setActivated(true)
      show({
        kind: 'success',
        title: 'MFA activado',
        description: 'Tu autenticación de dos pasos está lista.',
      })
      // Refrescar /api/me para propagar mfaEnabled=true al AuthProvider.
      await refresh()
      // Navigate diferido para que el alert sea observable (1.5s) — a11y + E2E.
      setTimeout(() => navigate('/app', { replace: true }), 1500)
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError && err.status === 401
          ? 'Código inválido. Reintenta con el código actual de tu app.'
          : err instanceof ApiError
            ? 'No se pudo verificar el código.'
            : 'Error de conexión con el servidor.'
      setVerifyError(msg)
    },
  })

  const handleVerify = async (code: string) => {
    if (!enrollData) return
    setVerifyError(null)
    await verifyMutation.mutateAsync({ secretId: enrollData.secretId, code })
  }

  const handleEnrollClick = () => {
    setEnrollError(null)
    enrollMutation.mutate()
  }

  return (
    <main
      className="mx-auto flex w-full max-w-xl flex-col gap-4 p-4 sm:p-6"
      data-testid="mfa-setup-container"
    >
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[color:var(--color-fitlab-blue)]" aria-hidden />
            Configurar autenticación de dos pasos (MFA)
          </CardTitle>
          <CardDescription>
            Protege tu cuenta con un segundo factor obligatorio para administradores.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Estado inicial: botón para iniciar enrollment */}
          {!enrollData && !enrollMutation.isPending && !enrollError && (
            <div className="flex flex-col items-start gap-3">
              <p className="text-sm text-slate-600">
                Al configurar MFA, te mostraremos un código QR que deberás escanear con tu app de
                autenticación (Google Authenticator, Authy, 1Password).
              </p>
              <Button
                onClick={handleEnrollClick}
                size="lg"
                leftIcon={<ShieldCheck className="h-4 w-4" />}
                data-testid="mfa-enroll-button"
              >
                Configurar MFA
              </Button>
            </div>
          )}

          {/* Loading generando */}
          {!enrollData && enrollMutation.isPending && (
            <div
              className="flex min-h-[16rem] items-center justify-center gap-2 text-slate-500"
              data-testid="mfa-qr-loading"
              role="status"
              aria-live="polite"
            >
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
              <span>Generando código QR…</span>
            </div>
          )}

          {/* Error enroll */}
          {enrollError && (
            <div
              role="alert"
              className="flex flex-col gap-3 rounded-md border border-[color:var(--color-fitlab-red)]/30 bg-[color:var(--color-fitlab-red)]/5 p-4 text-sm text-[color:var(--color-fitlab-red-hover)]"
              data-testid="mfa-enroll-error"
            >
              <p>{enrollError}</p>
              <Button size="sm" variant="outline" onClick={handleEnrollClick}>
                Reintentar
              </Button>
            </div>
          )}

          {/* Activado: mensaje persistente post-confirm (role=alert a11y + spec AC-01) */}
          {activated && (
            <div
              role="alert"
              className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-4 text-sm text-green-900"
              data-testid="mfa-activated"
            >
              <ShieldCheck className="h-5 w-5 shrink-0" aria-hidden />
              <p className="font-medium">MFA activado · autenticación de dos pasos habilitada.</p>
            </div>
          )}

          {/* QR + form verify */}
          {enrollData && !activated && (
            <div data-testid="mfa-qr-code">
              {/* URL otpauth visible para apps sin cámara (spec mfa-setup-flow expect visible) */}
              <p
                className="mb-3 break-all rounded-md border border-slate-200 bg-slate-50 p-2 font-mono text-[10px] text-slate-600"
                data-testid="mfa-otpauth-url"
                aria-label="URL alternativa para ingresar manualmente"
              >
                {enrollData.otpauthUrl}
              </p>
              <QrTotpSetup
                qrDataUri={enrollData.qrDataUri}
                secretPreview={extractSecretPreview(enrollData.otpauthUrl)}
                onVerify={handleVerify}
                isVerifying={verifyMutation.isPending}
                errorMessage={verifyError}
              />
            </div>
          )}
        </CardContent>
      </Card>
    </main>
  )
}

/**
 * Extrae los primeros 4 + últimos 4 chars del secret base32 del otpauth URL
 * para mostrar un preview (backup manual). Nunca secret completo.
 */
function extractSecretPreview(otpauthUrl: string): string {
  try {
    const url = new URL(otpauthUrl)
    const secret = url.searchParams.get('secret') ?? ''
    if (secret.length < 10) return '****'
    return `${secret.slice(0, 4)}…${secret.slice(-4)}`
  } catch {
    return '****'
  }
}
